import { useState, useEffect } from 'react';
import axios from 'axios';

export interface SystemActivity {
  id: string;
  type: 'user' | 'post' | 'contact' | 'settings' | 'backup' | 'login' | 'error';
  action: string;
  description: string;
  timestamp: string;
  user?: {
    name: string;
    email: string;
  };
  metadata?: {
    ip?: string;
    userAgent?: string;
    location?: string;
    device?: string;
    type?: string;
    version?: string;
    section?: string;
    theme?: string;
    status?: string;
    size?: string;
    duration?: string;
    uptime?: string;
    connections?: string;
    responseTime?: string;
    details?: any;
  };
}

export const useSystemActivities = () => {
  const [activities, setActivities] = useState<SystemActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      
      // Buscar dados reais da API
      const response = await axios.get('/activities');
      if (response.data && response.data.activities) {
        setActivities(response.data.activities);
        setError(null);
        console.log('✅ Atividades carregadas da API:', response.data.activities.length);
      } else {
        throw new Error('Resposta inválida da API');
      }
    } catch (err: any) {
      console.error('Erro ao buscar atividades da API:', err);
      
      // Em caso de erro, usar dados simulados mais realistas
      const mockActivities: SystemActivity[] = [
        {
          id: 'system_login',
          type: 'login',
          action: 'admin_login',
          description: 'Login administrativo realizado',
          timestamp: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
          user: { name: 'Admin', email: 'admin@neia.com' },
          metadata: { 
            ip: '192.168.1.100', 
            userAgent: 'Chrome/91.0',
            location: 'São Paulo, BR',
            device: 'Desktop'
          }
        },
        {
          id: 'system_startup',
          type: 'backup',
          action: 'system_started',
          description: 'Sistema iniciado com sucesso',
          timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
          user: { name: 'Sistema', email: 'system@neia.com' },
          metadata: { 
            type: 'automatic', 
            version: '1.0.0',
            status: 'running',
            uptime: '1h 30m'
          }
        },
        {
          id: 'settings_loaded',
          type: 'settings',
          action: 'settings_loaded',
          description: 'Configurações do sistema carregadas',
          timestamp: new Date(Date.now() - 10 * 60 * 1000).toISOString(),
          user: { name: 'Sistema', email: 'system@neia.com' },
          metadata: { 
            section: 'appearance', 
            theme: 'light'
          }
        },
        {
          id: 'database_connected',
          type: 'backup',
          action: 'database_connected',
          description: 'Conexão com banco de dados estabelecida',
          timestamp: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
          user: { name: 'Sistema', email: 'system@neia.com' },
          metadata: { 
            type: 'automatic', 
            status: 'connected',
            connections: '5/100',
            responseTime: '12ms'
          }
        }
      ];
      
      setActivities(mockActivities);
      setError('Usando dados simulados - API indisponível');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities();
  }, []);

  const getActivityIcon = (type: SystemActivity['type']) => {
    switch (type) {
      case 'user':
        return '👤';
      case 'post':
        return '📝';
      case 'contact':
        return '📧';
      case 'settings':
        return '⚙️';
      case 'backup':
        return '💾';
      case 'login':
        return '🔐';
      case 'error':
        return '⚠️';
      default:
        return '📋';
    }
  };

  const getActivityColor = (type: SystemActivity['type']) => {
    switch (type) {
      case 'user':
        return 'text-blue-600 bg-blue-100';
      case 'post':
        return 'text-green-600 bg-green-100';
      case 'contact':
        return 'text-purple-600 bg-purple-100';
      case 'settings':
        return 'text-orange-600 bg-orange-100';
      case 'backup':
        return 'text-indigo-600 bg-indigo-100';
      case 'login':
        return 'text-cyan-600 bg-cyan-100';
      case 'error':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Agora mesmo';
    if (diffInMinutes < 60) return `${diffInMinutes} min atrás`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h atrás`;
    return `${Math.floor(diffInMinutes / 1440)} dias atrás`;
  };

  return {
    activities,
    loading,
    error,
    fetchActivities,
    getActivityIcon,
    getActivityColor,
    formatTimestamp
  };
};

export default useSystemActivities;
